export const INVOICE_VALUE_CEILING = 120000;
export const DRIVER_AGE_MIN = 18;
export const DRIVER_AGE_MAX = 55;
export const BATTERY_IMEI_LENGTH = 15;

export const roundUpToStep = (value, step) =>
  Math.ceil(value / (step || 1)) * (step || 1);

export const buildDownpaymentOptions = (downPaymentConfig) => {
  if (!downPaymentConfig) return [];
  const { min, max, step } = downPaymentConfig;
  const options = [];
  for (let value = min; value <= max; value += step) {
    options.push(value);
  }
  return options;
};

export const buildTenureOptions = (tenures) =>
  (tenures || []).map((tenure) => ({
    label: `${tenure} months`,
    value: tenure,
  }));

export const buildBatteryTypeOptions = (batteryTypes) =>
  (batteryTypes || []).map((batteryType) => ({
    label: batteryType,
    value: batteryType,
  }));

export const buildCityOptions = (emiAdminConfig) => {
  const cities = new Set();
  Object.values(emiAdminConfig?.financiers || {}).forEach((financier) => {
    (financier.eligibility?.cities || []).forEach((city) => cities.add(city));
  });
  return Array.from(cities)
    .sort()
    .map((city) => ({ label: city, value: city }));
};

export const getAverageInterestRate = (emiAdminConfig) => {
  const financiers = Object.values(emiAdminConfig?.financiers || {});
  if (!financiers.length) return null;
  const total = financiers.reduce((sum, f) => sum + (f.interestRate || 0), 0);
  return total / financiers.length;
};

export const getFinancierEligibility = (
  financierConfig,
  { cibilScore, downpayment, city },
) => {
  const eligibility = financierConfig?.eligibility || {};
  const score = Number(cibilScore);
  const minCIBIL = eligibility.minCIBIL ?? 0;
  const maxCIBIL = eligibility.maxCIBIL ?? Infinity;

  if (!score || score < minCIBIL || score > maxCIBIL) return false;

  if (downpayment && eligibility.downPayment) {
    const dp = Number(downpayment);
    if (dp < eligibility.downPayment.min || dp > eligibility.downPayment.max) {
      return false;
    }
  }

  if (city && eligibility.cities?.length) {
    if (!eligibility.cities.includes(String(city).toUpperCase())) {
      return false;
    }
  }

  return true;
};

export const getEligibleFinanciers = (
  emiAdminConfig,
  { cibilScore, downpayment, city },
) => {
  const financiers = emiAdminConfig?.financiers || {};
  return Object.entries(financiers)
    .filter(([, config]) =>
      getFinancierEligibility(config, { cibilScore, downpayment, city }),
    )
    .map(([code, config]) => ({ code, ...config }));
};

export const getBatteryOverride = (
  emiAdminConfig,
  batteryType,
  financierCode,
) => emiAdminConfig?.batteryOverrides?.[batteryType]?.[financierCode] || null;

const calculateChargesTotal = (charges, { invoiceValue, loanAmount }) =>
  (charges || []).reduce((total, charge) => {
    let base = 0;
    if (charge.type === "rate_on_invoice") base = invoiceValue * charge.value;
    if (charge.type === "rate_on_loan") base = loanAmount * charge.value;
    if (charge.type === "fixed") base = charge.value;
    const gst = base * (charge.gstRate || 0);
    return total + base + gst;
  }, 0);

const calculateReducingBalanceEMI = (principal, annualRate, tenureMonths) => {
  const monthlyRate = (annualRate || 0) / 12;
  if (!monthlyRate || !tenureMonths || principal <= 0) return 0;
  const factor = Math.pow(1 + monthlyRate, tenureMonths);
  return (principal * monthlyRate * factor) / (factor - 1);
};

export const calculateFinancierQuote = ({
  emiAdminConfig,
  financierCode,
  batteryType,
  invoiceValue,
  downpayment,
  tenureMonths,
}) => {
  const financier = emiAdminConfig?.financiers?.[financierCode];
  if (!financier) return null;

  const override = getBatteryOverride(
    emiAdminConfig,
    batteryType,
    financierCode,
  );
  const targetNetBank = override?.targetNetBank ?? financier.targetNetBank;

  let effectiveInvoiceValue = Number(invoiceValue || 0);
  let effectiveDownpayment = Number(downpayment || 0);

  if (financier.invoiceFormula === "GROSS_UP" && financier.invoiceDivisor) {
    effectiveInvoiceValue = targetNetBank / financier.invoiceDivisor;
    if (financier.dpAdjustmentRate) {
      effectiveDownpayment +=
        effectiveInvoiceValue * financier.dpAdjustmentRate;
    }
  }

  const loanAmount = effectiveInvoiceValue - effectiveDownpayment;
  const chargesTotal = calculateChargesTotal(financier.charges, {
    invoiceValue: effectiveInvoiceValue,
    loanAmount,
  });

  const rawEmi = calculateReducingBalanceEMI(
    loanAmount,
    financier.interestRate,
    tenureMonths,
  );
  const emi = roundUpToStep(rawEmi, financier.emiRoundTo);

  return {
    financierCode,
    displayName: financier.displayName,
    effectiveInvoiceValue,
    effectiveDownpayment,
    loanAmount,
    chargesTotal,
    emi,
    targetNetBank,
  };
};
